import React, { ReactElement, useContext } from 'react';
import { InputNumber as AntInputNumber } from 'antd';
import styled from 'styled-components';
import { colorsPalette } from '../../styles/colors-palette';
import { moneyUtils } from '../../../infrastructure/utils/money-utils';
import { ValidationContext } from './Validation';
import { Skeleton } from './Skeleton';

type InputNumberValueType = number;
type InputNumberType = 'money';
type InputNumberSizeType = 'large' | 'middle' | 'small';

interface CustomStyle {
  width?: CSSSize;
  fontSize?: CSSSize;
}

interface InputProps<Value extends InputNumberValueType> {
  value?: Value;
  onChange?: (value: Value) => void;
  placeholder?: string;
  type?: InputNumberType;
  addonAfter?: React.ReactNode;
  disabled?: boolean;
  keyboard?: boolean;
  className?: string;
  skeletonWidth?: CSSSize;
  maxLength?: number;
  formatter?: (value: Value | undefined, info: { userTyping: boolean; input: string }) => string;
  parser?: (displayValue: string | undefined) => Value;
  size?: InputNumberSizeType;
  precision?: number;
  hiddenSteps?: boolean;
  min?: Value;
  max?: Value;
  loading?: boolean;
  customStyle?: CustomStyle;
}

function InputNumber<Value extends InputNumberValueType>({
  value,
  skeletonWidth,
  onChange,
  type,
  size,
  placeholder,
  addonAfter,
  disabled,
  className,
  maxLength,
  formatter,
  keyboard,
  hiddenSteps,
  precision,
  parser,
  min,
  max,
  loading,
  customStyle,
}: InputProps<Value>): ReactElement {
  const { validateStatus } = useContext(ValidationContext);
  const antOnChange: (value: Value | null) => void = value => {
    onChange && onChange(value as Value);
  };

  const formatToMoney = (value: Value | undefined): string => {
    return moneyUtils.formatMoneyString(`${value}`, true);
  };

  const parseToNumber = (value: string | undefined): Value => {
    return moneyUtils.parseStringWithSpacesToMoney(value) as Value;
  };

  if (loading) {
    return (
      <SkeletonWrapper>
        <StyledSkeleton width={skeletonWidth} className={className} />
      </SkeletonWrapper>
    );
  }

  return (
    <StyledInputNumber
      customstyle={customStyle}
      addonAfter={addonAfter}
      value={value}
      size={size ? size : 'large'}
      onChange={value => antOnChange(value as Value)}
      formatter={(value, info) => {
        if (type === 'money') {
          return formatToMoney(value as Value) as string;
        }

        return formatter ? formatter(value as Value, info) : `${value}`;
      }}
      parser={type === 'money' ? parseToNumber : parser}
      placeholder={placeholder}
      disabled={disabled}
      keyboard={keyboard}
      maxLength={maxLength}
      className={className}
      precision={precision}
      min={min}
      max={max}
      controls={!hiddenSteps}
      status={validateStatus}
    />
  );
}

const SkeletonWrapper = styled.div`
  height: 40px;
`;
const StyledSkeleton = styled(Skeleton)`
  &.ant-skeleton.ant-skeleton-element .ant-skeleton-input {
    height: 40px;
    vertical-align: middle;
    border-radius: 8px;
  }
`;

const StyledInputNumber = styled(AntInputNumber)<{ customstyle?: CustomStyle }>`
  &.ant-input-number {
    width: ${props => props.customstyle?.width};
    font-size: ${props => props.customstyle?.fontSize};
  }

  &.ant-input-number:focus,
  &.ant-input-number-focused {
    border-color: ${colorsPalette.dustyBlue500};
  }

  &.ant-input-number-group .ant-input-number {
    width: ${props => props.customstyle?.width ?? '100%'};
  }

  &.ant-input-number-group-wrapper {
    width: ${props => props.customstyle?.width ?? '100%'};
  }

  &.ant-input-number:hover {
    border-color: ${colorsPalette.dustyBlue500};
  }
`;

export default InputNumber;
